import { useLocation, useNavigate } from "react-router";
import { To } from "react-router-dom";
import classNames from "classnames/bind";
import { useIntl } from "react-intl";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { ButtonLink, ButtonLinkProps } from "app/components/ui/ButtonLink/ButtonLink";
import { Button, ButtonProps } from "app/components/ui/Button/Button";
import { ChevronLeftIcon } from "app/components/icons/navigation/ChevronLeftIcon";
import styles from "./BackButton.module.scss";

const cx = classNames.bind(styles);

type BackButtonProps = {
	to?: To;
	className?: string;
	onClick?: () => void;
} & (
	| Omit<ButtonLinkProps, "to" | "className" | "onClick">
	| Omit<ButtonProps, "className" | "onClick">
);

export const BackButton = ({ to, className, onClick, ...props }: BackButtonProps) => {
	const { track } = useAnalytics();
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const { formatMessage } = useIntl();

	const handleClick = () => {
		track("back_button_header_click", { url: pathname });
		onClick?.();

		if (!to) {
			navigate(-1);
		}
	};

	const startIcon = <ChevronLeftIcon className={cx("back-button__icon")} />;
	const ariaLabel = formatMessage({ id: "general.back" });

	if (to) {
		return (
			<ButtonLink
				variant="borderless"
				size="icon-lg"
				startIcon={startIcon}
				aria-label={ariaLabel}
				data-testid="back-button"
				onClick={handleClick}
				className={cx("back-button", className)}
				to={to}
				replace
				{...(props as Omit<ButtonLinkProps, "to" | "className" | "onClick">)}
			/>
		);
	}

	return (
		<Button
			variant="borderless"
			size="icon-lg"
			startIcon={startIcon}
			aria-label={ariaLabel}
			data-testid="back-button"
			onClick={handleClick}
			className={cx("back-button", className)}
			{...(props as Omit<ButtonProps, "asChild" | "loading" | "className" | "onClick">)}
		/>
	);
};
